/**
 * Analytics tracking service for micro-conversions and user behavior
 * Supports Google Analytics 4 via react-ga4 and can be extended for other platforms
 *
 * COMPREHENSIVE TRACKING IMPLEMENTATION
 * Updated: 2025-12-12
 */

import ReactGA from 'react-ga4';

// ============================================
// GA4 INITIALIZATION CHECK
// ============================================

let isGAInitialized = false;

// Check if ReactGA is initialized (called from App.jsx)
const checkGAInitialized = (): boolean => {
  if (isGAInitialized) return true;

  // ReactGA.isInitialized is available in react-ga4
  try {
    // @ts-ignore - isInitialized exists but may not be typed
    isGAInitialized = ReactGA.isInitialized?.() || true; // Assume initialized if method doesn't exist
  } catch {
    isGAInitialized = true; // Assume initialized to not block tracking
  }

  return isGAInitialized;
};

// ============================================
// CORE TRACKING FUNCTION
// ============================================

export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  try {
    // Use react-ga4's event method (preferred)
    if (typeof window !== 'undefined') {
      ReactGA.event(eventName, properties);
    }
  } catch (error) {
    // Silently fail - don't let analytics errors break the app
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to track event:', eventName, error);
    }
  }
};

// ============================================
// PAGE VIEW TRACKING FOR SPA
// ============================================

export const trackGAPageView = (path: string, title?: string) => {
  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to track page view:', path, error);
    }
  }
};

// ============================================
// AUTHENTICATION TRACKING
// ============================================

export const trackAuth = {
  loginStart: (page: string = 'signin') => {
    trackEvent('Login_Start', { page, timestamp: Date.now() });
  },

  loginSubmit: (method: string = 'email') => {
    trackEvent('Login_Submit', { method, timestamp: Date.now() });
  },

  loginSuccess: (userId?: string) => {
    trackEvent('Login_Success', { userId: userId || 'anonymous', timestamp: Date.now() });
  },

  loginFailure: (error: string) => {
    trackEvent('Login_Failure', { error, timestamp: Date.now() });
  },

  logout: (userId?: string) => {
    trackEvent('Logout', { userId: userId || 'anonymous', timestamp: Date.now() });
  },

  signupStart: (page: string = 'signup') => {
    trackEvent('Signup_Start', { page, timestamp: Date.now() });
  },

  signupSubmit: (hasAgreedTerms: boolean = true) => {
    trackEvent('Signup_Submit', { hasAgreedTerms, timestamp: Date.now() });
  },

  signupSuccess: (userId?: string) => {
    trackEvent('Signup_Success', { userId: userId || 'anonymous', timestamp: Date.now() });
  },

  signupFailure: (error: string) => {
    trackEvent('Signup_Failure', { error, timestamp: Date.now() });
  },

  passwordResetRequest: (email?: string) => {
    trackEvent('Password_Reset_Request', {
      email: email ? 'provided' : 'not_provided',
      timestamp: Date.now()
    });
  },

  passwordResetSuccess: () => {
    trackEvent('Password_Reset_Success', { timestamp: Date.now() });
  },

  passwordResetFailure: (error: string) => {
    trackEvent('Password_Reset_Failure', { error, timestamp: Date.now() });
  }
};

// ============================================
// NAVIGATION TRACKING
// ============================================

export const trackNavigation = {
  linkClick: (linkText: string, destination: string, location: string) => {
    trackEvent('Link_Click', {
      linkText,
      destination,
      location,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  buttonClick: (buttonId: string, buttonText: string, location: string) => {
    trackEvent('Button_Click', {
      buttonId,
      buttonText,
      location,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  menuOpen: (menuName: string) => {
    trackEvent('Menu_Open', { menuName, page: window.location.pathname, timestamp: Date.now() });
  },

  menuClose: (menuName: string) => {
    trackEvent('Menu_Close', { menuName, page: window.location.pathname, timestamp: Date.now() });
  },

  navItemClick: (itemName: string, section: string) => {
    trackEvent('Nav_Item_Click', {
      itemName,
      section,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// VIDEO TRACKING
// ============================================

export const trackVideo = {
  play: (videoId: string, videoUrl: string, location: string) => {
    trackEvent('Video_Play', {
      videoId,
      videoUrl,
      location,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  pause: (videoId: string, currentTime: number, duration: number) => {
    trackEvent('Video_Pause', {
      videoId,
      currentTime,
      duration,
      percentComplete: Math.round((currentTime / duration) * 100),
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  seek: (videoId: string, fromTime: number, toTime: number) => {
    trackEvent('Video_Seek', {
      videoId,
      fromTime,
      toTime,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  progress: (videoId: string, percent: number, currentTime: number, duration: number) => {
    trackEvent('Video_Progress', {
      videoId,
      percent,
      currentTime,
      duration,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  complete: (videoId: string, duration: number, watchTime: number) => {
    trackEvent('Video_Complete', {
      videoId,
      duration,
      watchTime,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  error: (videoId: string, videoUrl: string, error: string) => {
    trackEvent('Video_Error', {
      videoId,
      videoUrl,
      error,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  muteToggle: (videoId: string, isMuted: boolean) => {
    trackEvent('Video_Mute_Toggle', {
      videoId,
      isMuted,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  fullscreenToggle: (videoId: string, isFullscreen: boolean) => {
    trackEvent('Video_Fullscreen_Toggle', {
      videoId,
      isFullscreen,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  theaterModeToggle: (videoId: string, isTheaterMode: boolean) => {
    trackEvent('Video_Theater_Mode', {
      videoId,
      isTheaterMode,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// FORM TRACKING
// ============================================

export const trackForm = {
  formStart: (formId: string, formName: string) => {
    trackEvent('Form_Start', {
      formId,
      formName,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  fieldFocus: (formId: string, fieldName: string) => {
    trackEvent('Form_Field_Focus', {
      formId,
      fieldName,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  fieldComplete: (formId: string, fieldName: string) => {
    trackEvent('Form_Field_Complete', {
      formId,
      fieldName,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  formSubmit: (formId: string, formName: string, success: boolean) => {
    trackEvent('Form_Submit', {
      formId,
      formName,
      success,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  formError: (formId: string, formName: string, error: string, field?: string) => {
    trackEvent('Form_Error', {
      formId,
      formName,
      error,
      field: field || 'general',
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  formAbandon: (formId: string, formName: string, lastField: string, timeSpent: number) => {
    trackEvent('Form_Abandon', {
      formId,
      formName,
      lastField,
      timeSpent,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// MODAL TRACKING
// ============================================

export const trackModal = {
  open: (modalId: string, modalName: string, trigger: string) => {
    trackEvent('Modal_Open', {
      modalId,
      modalName,
      trigger,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  close: (modalId: string, modalName: string, method: string = 'close_button') => {
    trackEvent('Modal_Close', {
      modalId,
      modalName,
      method,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  action: (modalId: string, modalName: string, action: string) => {
    trackEvent('Modal_Action', {
      modalId,
      modalName,
      action,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// ENROLLMENT TRACKING (HIGH PRIORITY)
// ============================================

export const trackEnrollment = {
  start: (courseId: string, bundleId: string | null, source: string) => {
    trackEvent('Enrollment_Start', {
      courseId,
      bundleId,
      source,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  stepComplete: (step: string, stepNumber: number, courseId: string) => {
    trackEvent('Enrollment_Step_Complete', {
      step,
      stepNumber,
      courseId,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  emailEntered: (courseId: string) => {
    trackEvent('Enrollment_Email_Entered', {
      courseId,
      step: 'email',
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  paymentInfoEntered: (courseId: string) => {
    trackEvent('Enrollment_Payment_Info', {
      courseId,
      step: 'payment',
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  paymentStart: (amount: number, productType: string, productId: string) => {
    trackEvent('Payment_Start', {
      amount,
      productType,
      productId,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  paymentSuccess: (transactionId: string, amount: number, productType: string, productId: string) => {
    trackEvent('Payment_Success', {
      transactionId,
      amount,
      productType,
      productId,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  paymentFailure: (error: string, errorCode: string, amount: number, productId: string) => {
    trackEvent('Payment_Failure', {
      error,
      errorCode,
      amount,
      productId,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  complete: (courseId: string, userId: string, transactionId: string) => {
    trackEvent('Enrollment_Complete', {
      courseId,
      userId,
      transactionId,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  abandon: (step: string, timeOnPage: number, courseId: string) => {
    trackEvent('Enrollment_Abandon', {
      step,
      timeOnPage,
      courseId,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// CLASSROOM TRACKING (HIGH VALUE)
// ============================================

export const trackClassroom = {
  enter: (courseId: string, courseName: string) => {
    trackEvent('Classroom_Enter', {
      courseId,
      courseName,
      timestamp: Date.now()
    });
  },

  moduleSelect: (moduleIndex: number, moduleName: string, courseId: string) => {
    trackEvent('Module_Select', {
      moduleIndex,
      moduleName,
      courseId,
      timestamp: Date.now()
    });
  },

  lessonSelect: (lessonIndex: number, lessonName: string, moduleId: string, courseId: string) => {
    trackEvent('Lesson_Select', {
      lessonIndex,
      lessonName,
      moduleId,
      courseId,
      timestamp: Date.now()
    });
  },

  topicSelect: (topicIndex: number, topicName: string, lessonId: string, courseId: string) => {
    trackEvent('Topic_Select', {
      topicIndex,
      topicName,
      lessonId,
      courseId,
      timestamp: Date.now()
    });
  },

  videoPlay: (topicId: string, videoUrl: string, courseId: string) => {
    trackEvent('Classroom_Video_Play', {
      topicId,
      videoUrl,
      courseId,
      timestamp: Date.now()
    });
  },

  videoPause: (topicId: string, currentTime: number, duration: number, courseId: string) => {
    trackEvent('Classroom_Video_Pause', {
      topicId,
      currentTime,
      duration,
      percentComplete: Math.round((currentTime / duration) * 100),
      courseId,
      timestamp: Date.now()
    });
  },

  videoProgress: (topicId: string, progress: number, courseId: string) => {
    trackEvent('Classroom_Video_Progress', {
      topicId,
      progress,
      courseId,
      timestamp: Date.now()
    });
  },

  videoComplete: (topicId: string, watchTime: number, courseId: string) => {
    trackEvent('Classroom_Video_Complete', {
      topicId,
      watchTime,
      courseId,
      timestamp: Date.now()
    });
  },

  lessonComplete: (lessonId: string, lessonName: string, courseId: string) => {
    trackEvent('Lesson_Complete', {
      lessonId,
      lessonName,
      courseId,
      timestamp: Date.now()
    });
  },

  courseComplete: (courseId: string, courseName: string, totalTime: number) => {
    trackEvent('Course_Complete', {
      courseId,
      courseName,
      totalTime,
      timestamp: Date.now()
    });
  },

  sidebarToggle: (collapsed: boolean, courseId: string) => {
    trackEvent('Sidebar_Toggle', {
      collapsed,
      courseId,
      timestamp: Date.now()
    });
  },

  navigationPrevious: (fromTopic: string, toTopic: string, courseId: string) => {
    trackEvent('Navigation_Previous', {
      fromTopic,
      toTopic,
      courseId,
      timestamp: Date.now()
    });
  },

  navigationNext: (fromTopic: string, toTopic: string, courseId: string) => {
    trackEvent('Navigation_Next', {
      fromTopic,
      toTopic,
      courseId,
      timestamp: Date.now()
    });
  }
};

// ============================================
// SEARCH TRACKING
// ============================================

export const trackSearch = {
  start: (location: string) => {
    trackEvent('Search_Start', {
      location,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  submit: (query: string, location: string, resultsCount: number) => {
    trackEvent('Search_Submit', {
      query,
      location,
      resultsCount,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  resultClick: (query: string, resultIndex: number, resultTitle: string) => {
    trackEvent('Search_Result_Click', {
      query,
      resultIndex,
      resultTitle,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  noResults: (query: string, location: string) => {
    trackEvent('Search_No_Results', {
      query,
      location,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// ACCORDION/FAQ TRACKING
// ============================================

export const trackAccordion = {
  expand: (accordionId: string, title: string, section: string) => {
    trackEvent('Accordion_Expand', {
      accordionId,
      title,
      section,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  collapse: (accordionId: string, title: string, section: string) => {
    trackEvent('Accordion_Collapse', {
      accordionId,
      title,
      section,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  categoryExpand: (categoryId: string, categoryName: string) => {
    trackEvent('FAQ_Category_Expand', {
      categoryId,
      categoryName,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  questionExpand: (questionId: string, questionText: string, category: string) => {
    trackEvent('FAQ_Question_Expand', {
      questionId,
      questionText: questionText.substring(0, 100),
      category,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// ERROR TRACKING
// ============================================

export const trackError = {
  /**
   * Track exceptions/fatal errors (used by ErrorBoundary)
   * This sends to GA4's exception event type
   */
  exception: (description: string, fatal: boolean = true, additionalProps?: Record<string, any>) => {
    try {
      // GA4 exception event
      ReactGA.event('exception', {
        description: description?.substring(0, 150) || 'Unknown error',
        fatal,
        ...additionalProps
      });

      // Also track as custom event for more detail
      trackEvent('Error_Exception', {
        description,
        fatal,
        page: window.location.pathname,
        ...additionalProps,
        timestamp: Date.now()
      });
    } catch (e) {
      // Silently fail
      console.warn('[Analytics] Failed to track exception:', e);
    }
  },

  pageError: (page: string, error: string, stack?: string) => {
    trackEvent('Error_Page_Load', {
      page,
      error,
      stack: stack?.substring(0, 500),
      timestamp: Date.now()
    });
  },

  apiError: (endpoint: string, status: number, error: string, method: string = 'GET') => {
    trackEvent('Error_API', {
      endpoint,
      status,
      error,
      method,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  paymentError: (errorCode: string, message: string, amount?: number) => {
    trackEvent('Error_Payment', {
      errorCode,
      message,
      amount,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  videoError: (videoUrl: string, error: string, videoId?: string) => {
    trackEvent('Error_Video', {
      videoUrl,
      error,
      videoId,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  authError: (type: string, error: string) => {
    trackEvent('Error_Auth', {
      type,
      error,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  formError: (formId: string, error: string, field?: string) => {
    trackEvent('Error_Form', {
      formId,
      error,
      field,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  generalError: (error: string, context: string, stack?: string) => {
    trackEvent('Error_General', {
      error,
      context,
      stack: stack?.substring(0, 500),
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// DASHBOARD TRACKING
// ============================================

export const trackDashboard = {
  view: (section: string) => {
    trackEvent('Dashboard_View', {
      section,
      timestamp: Date.now()
    });
  },

  quickActionClick: (action: string) => {
    trackEvent('Quick_Action_Click', {
      action,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  courseResume: (courseId: string, courseName: string) => {
    trackEvent('Course_Resume', {
      courseId,
      courseName,
      timestamp: Date.now()
    });
  },

  settingsUpdate: (field: string, success: boolean) => {
    trackEvent('Settings_Update', {
      field,
      success,
      timestamp: Date.now()
    });
  },

  billingAction: (action: string, details?: Record<string, any>) => {
    trackEvent('Billing_Action', {
      action,
      ...details,
      timestamp: Date.now()
    });
  }
};

// ============================================
// CTA TRACKING (MARKETING)
// ============================================

export const trackCTA = {
  click: (ctaId: string, ctaText: string, location: string, destination?: string) => {
    trackEvent('CTA_Click', {
      ctaId,
      ctaText,
      location,
      destination,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  heroCtaClick: (ctaText: string, page: string) => {
    trackEvent('Hero_CTA_Click', {
      ctaText,
      page,
      timestamp: Date.now()
    });
  },

  enrollCtaClick: (courseId: string, location: string, price?: number) => {
    trackEvent('Enroll_CTA_Click', {
      courseId,
      location,
      price,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// COURSE/BUNDLE CARD TRACKING
// ============================================

export const trackCourseCard = {
  click: (courseId: string, courseName: string, location: string) => {
    trackEvent('Course_Card_Click', {
      courseId,
      courseName,
      location,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  hover: (courseId: string, courseName: string) => {
    trackEvent('Course_Card_Hover', {
      courseId,
      courseName,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

export const trackBundleCard = {
  click: (bundleId: string, bundleName: string, location: string) => {
    trackEvent('Bundle_Card_Click', {
      bundleId,
      bundleName,
      location,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  },

  expand: (bundleId: string, bundleName: string) => {
    trackEvent('Bundle_Card_Expand', {
      bundleId,
      bundleName,
      page: window.location.pathname,
      timestamp: Date.now()
    });
  }
};

// ============================================
// LEGACY MICRO-CONVERSION TRACKING (PRESERVED)
// ============================================

export const trackMicroConversion = {
  videoPlay: (location: string = 'hero', course: string = 'PEM') => {
    trackEvent('Video_Play', { location, course });
  },

  ctaClick: (location: string, course: string = 'PEM') => {
    trackEvent('CTA_Click', { location, course });
  },

  scrollDepth: (depth: number, course: string = 'PEM') => {
    trackEvent('Scroll_Depth', { depth, course });
  },

  faqExpand: (question: string, course: string = 'PEM') => {
    trackEvent('FAQ_Expand', { question, course });
  },

  moduleClick: (moduleNumber: number, moduleTitle: string, course: string = 'PEM') => {
    trackEvent('Module_Click', { moduleNumber, moduleTitle, course });
  },

  testimonialView: (testimonialName: string, course: string = 'PEM') => {
    trackEvent('Testimonial_View', { testimonialName, course });
  },

  timerView: (course: string = 'PEM') => {
    trackEvent('Timer_View', { course });
  },

  pageView: (pageName: string, course: string = 'PEM') => {
    trackEvent('Page_View', { pageName, course });
  }
};

// ============================================
// SCROLL DEPTH TRACKING HOOK (ENHANCED)
// ============================================

export const useScrollDepthTracking = (pageName: string = 'unknown') => {
  const tracked = {
    depth25: false,
    depth50: false,
    depth75: false,
    depth90: false
  };

  const handleScroll = () => {
    const scrollPercent =
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

    if (scrollPercent > 25 && !tracked.depth25) {
      trackEvent('Scroll_Depth', { depth: 25, page: pageName });
      tracked.depth25 = true;
    }
    if (scrollPercent > 50 && !tracked.depth50) {
      trackEvent('Scroll_Depth', { depth: 50, page: pageName });
      tracked.depth50 = true;
    }
    if (scrollPercent > 75 && !tracked.depth75) {
      trackEvent('Scroll_Depth', { depth: 75, page: pageName });
      tracked.depth75 = true;
    }
    if (scrollPercent > 90 && !tracked.depth90) {
      trackEvent('Scroll_Depth', { depth: 90, page: pageName });
      tracked.depth90 = true;
    }
  };

  return handleScroll;
};

// ============================================
// PAGE VIEW TRACKING HELPER
// ============================================

export const trackPageView = (pageName: string, additionalProps?: Record<string, any>) => {
  trackEvent('Page_View', {
    pageName,
    url: window.location.pathname,
    referrer: document.referrer,
    ...additionalProps,
    timestamp: Date.now()
  });
};
